import { Col } from 'antd';
import { differenceInYears } from 'date-fns';
import { FormItem } from 'modules/common/components';
import { getIsRequiredInSchema } from 'modules/common/utils/yup/getIsRequiredInSchema';
import { getAllowedDonorAge } from 'modules/donor/utils/get-allowed-donor-age';
import { FC, useEffect, useMemo } from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFields } from './fields';
import { requiredFormSectionSchema } from './schema';

import { CreateDonorSchemaType } from '../schema';

type PropTypes = {
  fieldNamePrefix: string;
};

interface ICreateDonorFormErrors {
  [key: string]:
    | {
        [key: string]: FieldError | undefined;
      }
    | undefined;
}

const RequiredFormSection: FC<PropTypes> = ({ fieldNamePrefix }) => {
  const { t } = useTranslation('donor.CreateDonorForm.RequiredSection');

  const { control, formState, watch, resetField, trigger } =
    useFormContext<CreateDonorSchemaType>();

  const region = watch('required.region');
  const species = watch('required.species');
  const dateOfBirth = watch('required.dateOfBirth');

  const requiredFormSectionFields = useMemo(
    () => getFields({ region }),
    [fieldNamePrefix, region]
  );

  useEffect(() => {
    resetField('required.location');
  }, [region]);

  useEffect(() => {
    if (dateOfBirth) {
      trigger('required.dateOfBirth');
    }
  }, [species, dateOfBirth]);

  const now = new Date();
  const currentAge = differenceInYears(now, dateOfBirth);
  const donorAgeRange = getAllowedDonorAge(species);
  const showAgeWarning =
    Boolean(dateOfBirth) &&
    (currentAge < donorAgeRange.min || currentAge >= donorAgeRange.max);

  const isRequired = getIsRequiredInSchema(requiredFormSectionSchema);
  const errors = formState.errors as ICreateDonorFormErrors;

  return (
    <>
      {requiredFormSectionFields.map(({ Component, label, name }) => {
        const fieldName = `${fieldNamePrefix}.${name}`;
        const errorMessage = errors[fieldNamePrefix]?.[name]?.message;
        const required = isRequired(name);
        const isDateOfBirthField = name === 'dateOfBirth';

        const extraText = errorMessage
          ? errorMessage
          : isDateOfBirthField && showAgeWarning
          ? t('donorAgeIsOutOfRangeWarning', donorAgeRange)
          : undefined;

        const validateStatus =
          (errorMessage && 'error') ||
          (isDateOfBirthField && showAgeWarning && 'warning') ||
          undefined;

        return (
          <Col span={24} key={fieldName}>
            <FormItem
              label={label}
              extra={extraText}
              validateStatus={validateStatus}
              required={required}
            >
              <Component control={control} name={fieldName} />
            </FormItem>
          </Col>
        );
      })}
    </>
  );
};

export default RequiredFormSection;
